import { FC, useEffect, useState } from 'react';
import { fetchRecentTimeRecords, deleteTimeRecord } from '../../lib/timeRecords';
import { fetchUserProfiles } from '../../lib/users';
import { supabase } from '../../lib/supabase';
import type { TimeRecordListItem } from '../../types/timeRecord';
import type { UserProfileMap } from '../../types/user';
import TimeRecordItem from './TimeRecordItem';
import type { PostgrestError } from '@supabase/supabase-js';

const INITIAL_DISPLAY_COUNT = 31;

const TimeRecordList: FC = () => {
  const [records, setRecords] = useState<TimeRecordListItem[]>([]);
  const [displayedRecords, setDisplayedRecords] = useState<TimeRecordListItem[]>([]);
  const [userProfiles, setUserProfiles] = useState<UserProfileMap>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadRecords = async () => {
    setLoading(true);
    try {
      const [recordsResponse, profiles] = await Promise.all([
        fetchRecentTimeRecords(),
        fetchUserProfiles()
      ]);

      if (recordsResponse.error) throw recordsResponse.error;
      const allRecords = recordsResponse.data || [];
      setRecords(allRecords);
      setDisplayedRecords(allRecords.slice(0, INITIAL_DISPLAY_COUNT));
      setUserProfiles(profiles);
      setError(null);
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Failed to load records');
      setRecords([]);
      setDisplayedRecords([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRecords();
  }, []);

  const handleShowMore = () => {
    setDisplayedRecords(records);
  };

  const handleEdit = (date: string) => {
    window.location.href = `/?date=${date}`;
  };

  const handleDelete = async (date: string) => {
    if (!window.confirm('Are you sure you want to delete this record?')) {
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      setLoading(true);
      const { error } = await deleteTimeRecord(date, user.id);
      if (error) throw error;
      
      await loadRecords();
      setError(null);
    } catch (err) {
      console.error('Failed to delete record:', err);
      setError('Failed to delete record');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="animate-pulse space-y-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
              <div className="space-y-3">
                <div className="h-3 bg-gray-200 rounded w-3/4"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-2xl mx-auto p-4">
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error Loading Records</h3>
              <p className="mt-1 text-sm text-red-700">{error}</p>
              <button
                onClick={loadRecords}
                className="mt-2 text-sm font-medium text-red-600 hover:text-red-500"
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto p-4">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Recent Time Records</h2>
        <p className="mt-1 text-sm text-gray-500">
          Showing your {displayedRecords.length} most recent time records
          {records.length > displayedRecords.length && ` (${records.length} total)`}
        </p>
      </div>

      {displayedRecords.length === 0 ? (
        <div className="bg-white p-8 text-center rounded-lg shadow-sm border border-gray-100">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-900">No time records</h3>
          <p className="mt-1 text-sm text-gray-500">
            Get started by creating your first time record
          </p>
          <div className="mt-6">
            <a
              href="/"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700"
            >
              Create Time Record
            </a>
          </div>
        </div>
      ) : (
        <>
          <div className="space-y-4">
            {displayedRecords.map((record) => (
              <TimeRecordItem
                key={`${record.record_date}-${record.user_id}`}
                record={record}
                onEdit={handleEdit}
                onDelete={handleDelete}
                userProfiles={userProfiles}
              />
            ))}
          </div>

          {records.length > displayedRecords.length && (
            <div className="mt-6 text-center">
              <button
                onClick={handleShowMore}
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                Show All Records
                <svg 
                  className="ml-2 h-4 w-4" 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M19 9l-7 7-7-7" 
                  />
                </svg>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TimeRecordList;